import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { Breadcrumb, Pagination, Template } from '@components'
import EmptyRow from '@components/EmptyRow'
import Spinner from '@components/Spinner'
import Table from '@components/Table'
import Td from '@components/Td'
import Tr from '@components/Tr'
import { clientApi } from '@services/ClientApi'
import { formatCpfCnpj } from '@utils/helpers/CpfCnpjFormated'
import {
	createBreadcrumbLink,
	useCreateBreadcrumb,
	useQuery,
} from '@utils/hooks'

const styles = {
	container: 'mt-8',
	header: 'mb-6',
	title: 'text-2xl font-bold text-gray-800',
	subtitle: 'text-gray-500',
	infoGrid: 'grid grid-cols-1 md:grid-cols-2 gap-6 mb-8',
	infoItem: 'flex flex-col',
	infoLabel: 'text-sm text-gray-500',
	infoValue: 'font-medium text-gray-800',
}

export default function ClientDownloadHistory() {
	const { id } = useParams<{ id: string }>()
	const query = useQuery()
	const page = Number(query.get('page')) || 1

	const { data, isLoading } = clientApi.useGetClientDownloadHistoryQuery(
		{ clientId: id || '', page },
		{ skip: !id }
	)

	const allPages = useMemo(() => {
		return [
			createBreadcrumbLink('Clientes', '/clientes', 1),
			createBreadcrumbLink('Downloads', '/clientes/downloads', 2),
			createBreadcrumbLink(
				data?.user?.name || 'Detalhes',
				`/clientes/downloads/${id}`,
				3
			),
			createBreadcrumbLink(
				'Histórico',
				`/clientes/downloads/${id}/historico`,
				4
			),
		]
	}, [data?.user?.name, id])

	const pages = useCreateBreadcrumb(allPages)

	if (isLoading) {
		return (
			<Template>
				<div className='flex justify-center mt-10'>
					<Spinner size='md' />
				</div>
			</Template>
		)
	}

	if (!data) {
		return (
			<Template>
				<div>Dados não encontrados</div>
			</Template>
		)
	}

	const { user, meta } = data
	const history = data.data || []

	return (
		<Template>
			<Breadcrumb pages={pages} />
			<div className={styles.container}>
				<div className={styles.header}>
					<h1 className={styles.title}>Histórico Completo de Downloads</h1>
					<p className={styles.subtitle}>
						Todos os downloads registrados de {user.name}
					</p>
				</div>

				<div className={styles.infoGrid}>
					<div className={styles.infoItem}>
						<span className={styles.infoLabel}>Nome</span>
						<span className={styles.infoValue}>{user.name}</span>
					</div>
					<div className={styles.infoItem}>
						<span className={styles.infoLabel}>Email</span>
						<span className={styles.infoValue}>{user.email}</span>
					</div>
					<div className={styles.infoItem}>
						<span className={styles.infoLabel}>CPF/CNPJ</span>
						<span className={styles.infoValue}>
							{user.cpfCnpj ? formatCpfCnpj(user.cpfCnpj) : '-'}
						</span>
					</div>
					<div className={styles.infoItem}>
						<span className={styles.infoLabel}>Total de Downloads</span>
						<span className={styles.infoValue}>{meta.total}</span>
					</div>
				</div>

				<Table
					th={[
						{ label: 'Nome do Arquivo' },
						{ label: 'Data' },
						{ label: 'Módulo' },
						{ label: 'IP' },
					]}
				>
					{history.map((file) => (
						<Tr key={file.id}>
							<Td>{file.file?.name || '-'}</Td>
							<Td>{file.downloadedAt || '-'}</Td>
							<Td>{file.moduleName || '-'}</Td>
							<Td>{file.ip || '-'}</Td>
						</Tr>
					))}
					{history.length === 0 && (
						<EmptyRow message='Nenhum download registrado' tdAmount={4} />
					)}
				</Table>

				<div className='mt-4'>
					<Pagination
						pageCount={meta.lastPage || 0}
						maxAmount={meta.total || 0}
						perPage={meta.perPage || 0}
					/>
				</div>
			</div>
		</Template>
	)
}
